import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import html2canvas from 'html2canvas';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import routes from '../../routes';
import { extractWhitelabelSubdomain, renderCount, toTitleCase } from '../../utils/helpers';
import { formatDate, formatDateTime } from '../../utils/fudrLocalizationHelper';
import { getButtonHandlerCta, getErrorDescription, getErrorTitle } from '../../selectors';
import {
  fetchSubscribedGuestDealAction,
  updateDealGuestAction,
  fetchDealByIdAction,
} from '../../actions/app.action';
import {
  fetchOutletDetailsAction,
  clearApiErrorResponseAction,
  fetchAppStartupInfoAction,
} from '../../actions/common';
import { constants } from '../../constants/configuration';
import backIcon from '../../assets/images/arrow-back.svg';
import {
  apiResponseCodes,
  currencies,
  deals,
  restaurantDataDepthParams,
  offerTypeCode,
  dealTypes,
} from '../../constants/globals';
import couponExpiredStamp from '../../assets/images/icons/coupon-expired-stamp.svg';
import couponRedeemedStamp from '../../assets/images/icons/coupon-redeemed-stamp.svg';
import lockedStamp from '../../assets/images/icons/locked-stamp.svg';
import './CouponDetail.scss';

const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const CouponTnC = asyncRoute(() => import('../../components/CouponTnC'));
const ShareCoupon = asyncRoute(() => import('../../components/ShareCoupon'));
const Footer = asyncRoute(() => import('../../components/Footer'));
const CouponCodeCard = asyncRoute(() => import('../../components/Card/CouponCodeCard'));
const CommunicationInfo = asyncRoute(() => import('../../components/CommunicationInfo'));
const OutletTimingPopup = asyncRoute(() => import('../../components/Popup/OutletTiming'));
const StreaxCard = asyncRoute(() => import('../../components/Card/StreaxCard'));
const RedemptionDetailsPopup = asyncRoute(() => import('../../components/Popup/RedemptionDetails'));
const DownloadCouponPopup = asyncRoute(() => import('../../components/Popup/DownloadCouponPopup'));
const Button = asyncRoute(() => import('../../components/elements/Button'));

const couponDetailConfig = constants.couponDetail;

class DealSuccess extends Component {
  constructor(props) {
    super(props);
    this.state = {
      initialAPIInteractionDone: false, // Whether the API calls on mount have completed (irrespective of SUCCESS or ERROR response)
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      showOutletTimingPopup: false,
      showRedemptionDetailsPopup: false,
      outletWorkhours: [],
      dealGuestAPIInteractionDone: false,
      selectedStreakCouponId: '',
      openDownloadCouponPopup: false,
    };

    this.couponRef = React.createRef();
  }

  componentDidMount() {
    const pageSize = get(deals, 'defaultPageSize', 20);
    const { config, workhours, phoneNumbers } = restaurantDataDepthParams;
    const subdomain = extractWhitelabelSubdomain();
    const { match } = this.props;

    const appStartupReqBody = {
      subdomain,
      includeModules: ['engage', 'brand'],
    };
    this.props.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        let outletIds = [];
        const outlets = get(response, 'data.engageBrand.outlets', []);
        outlets.map((outlet) => outletIds.push(outlet.id));
        const couponCode = get(match, 'params.couponCode', '');
        const brandId = get(response, 'data.brandId', '');
        this.props.fetchSubscribedGuestDeal({ couponCode }).then((subscribedDealData) => {
          this.props.fetchDealById({ dealCode: get(subscribedDealData, 'dealId', '') });
          if (isEqual(get(subscribedDealData, 'dealType'), dealTypes.streak)) {
            const subDealGuestsObj = get(subscribedDealData, 'subDealGuests', []).find(
              (subDealGuest) => subDealGuest.id === subscribedDealData.activeDealGuestId,
            );
            if (!isEmpty(subDealGuestsObj)) {
              const streakCouponId = `streak-coupon-${subDealGuestsObj.dealSequence}`;
              this.setState({
                currentSelectCouponSequence: subDealGuestsObj.dealSequence,
                selectedStreakCouponId: streakCouponId,
              });
            }
          }
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
            let url = `/brands/${brandId}/outletDetails?size=${pageSize}&depth=${[
              config,
              workhours,
              phoneNumbers,
            ]}&outletIds=${outletIds}`;
            this.props.fetchOutletDetails({ url }).then(() => {
              // As the API call on mount has completed, mark the initialAPIInteractionDone as true
              this.setInitialAPIInteractionDone(true);
            });
          }
        });
      }
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { dealGuestAPIInteractionDone } = this.state;
    // Check for error response, if any, hide loader
    if (
      !isEmpty(get(this.props, 'errorResponse')) &&
      !isEqual(get(this.props, 'errorResponse'), get(prevProps, 'errorResponse'))
    ) {
      let { code, message } = get(this.props, 'errorResponse', {});
      this.setState({
        showErrorPopup: true,
        errorContent: {
          title: getErrorTitle({ source: couponDetailConfig, code }),
          description: getErrorDescription({
            source: couponDetailConfig,
            code,
            message,
          }),
          buttonCta: getButtonHandlerCta({ source: couponDetailConfig, code }),
        },
      });
    }

    if (!isEqual(dealGuestAPIInteractionDone, prevState.dealGuestAPIInteractionDone)) {
      const { selectedStreakCouponId } = this.state;
      const streakCouponElement = document.getElementById(selectedStreakCouponId);
      streakCouponElement && streakCouponElement.scrollIntoView(false);
    }
  }

  setInitialAPIInteractionDone = (flag) => {
    this.setState({
      initialAPIInteractionDone: flag,
      dealGuestAPIInteractionDone: true,
    });
  };

  handleErrorCTA = () => {
    this.props.history.push(`/${routes.containers.deals}`);
  };

  handleExceptionCTA = () => {
    this.props.history.push(`/${routes.containers.deals}`);
  };

  handelShareCTAClick = () => {
    const { appStartupInfo, deal } = this.props;
    const dealCode = get(deal, 'code', '');
    const brandId = get(appStartupInfo, 'brandId', '');
    const brandName = toTitleCase(get(appStartupInfo, 'brand.name', ''));
    const brandDealsLink =
      brandId && `https://${brandId}.fudr.in/${routes.containers.deal}/${dealCode}`;
    const shareText = `Hey, I got this amazing deal from ${brandName}. I think you will love them too! Click here to get it now: ${brandDealsLink}`;
    window.open(`https://api.whatsapp.com/send?text=${shareText}`);
  };

  handeBackClick = () => {
    this.props.history.push(`/${routes.containers.deals}`);
  };

  onCloseExceptionComponent = () => {
    this.props.clearApiErrorResponse().then(() => {
      this.setState({
        showErrorPopup: false,
        errorContent: {
          title: '',
          description: '',
          buttonCta: '',
        },
      });
    });
  };

  renderRedeemedCouponStatus = ({ type, dealItems, discountAmount }) => {
    switch (type) {
      case offerTypeCode.DirectDiscount:
        return `You have redeemed this coupon and saved ${currencies.INR.symbol}${discountAmount}`;
      case offerTypeCode.ItemFree:
      case offerTypeCode.ItemOffer:
      case offerTypeCode.ComboOffer:
        const applicableItem =
          dealItems &&
          dealItems.length > 0 &&
          get(dealItems.find((item) => item) || {}, 'itemName', '');
        return `You have redeemed this coupon and availed Free ${applicableItem}`;
      default:
        return;
    }
  };

  renderStreakCouponDescription = ({ subscribedDeal, sequenceNumber, activeDayCounter }) => {
    let couponDescription = '';
    const subDealGuests = get(subscribedDeal, 'subDealGuests', []);
    const subDealGuestsObj = subDealGuests.find(
      (subDealGuest) => subDealGuest.dealSequence === sequenceNumber,
    );
    const isDealExpired = get(subscribedDeal, 'isExpired', false);
    const dealExpireTime = get(subscribedDeal, 'expireTime', '');
    if (!isEmpty(subDealGuestsObj)) {
      const { couponCode, discountAmount, expireTime, subscribeTime, redeemTime, type, freeItems } =
        subDealGuestsObj;

      const dealItems =
        get(subscribedDeal, 'dealItems', []).length !== 0
          ? get(subscribedDeal, 'dealItems', [])
          : freeItems;

      couponDescription = this.renderCouponStatus({
        couponCode,
        discountAmount,
        isCouponRedeemed: redeemTime,
        dealEndTime: expireTime,
        isDealExpired,
        subscribeTime,
        dealItems,
        type,
        dealExpireTime,
      });
    } else {
      couponDescription = (
        <div className="coupon-code-card_coupon-status">
          <div className="coupon-code-card_coupon-status_label">
            <img src={lockedStamp} alt="locked-coupon" />
            <div className="coupon-code-card_coupon-status_text-font-14-bold">Locked</div>
          </div>
          <div className="coupon-code-card_coupon-status_locked">
            {`Unlock this coupon code after ${
              isEqual(activeDayCounter, 'PREV') ? renderCount(sequenceNumber - 1) : `1st`
            } Order`}
          </div>
        </div>
      );
    }
    return couponDescription;
  };

  renderCouponStatus = ({
    couponCode,
    discountAmount,
    isCouponRedeemed,
    dealEndTime,
    isDealExpired,
    subscribeTime,
    dealItems,
    type,
    dealExpireTime,
  }) => {
    const currentDate = moment().format();
    if (isCouponRedeemed) {
      return (
        <div className="coupon-code-card_coupon-status">
          <div className="coupon-code-card_coupon-status_label">
            <img src={couponRedeemedStamp} alt="redeemed-coupon" />
            <div className="coupon-code-card_coupon-status_text-font-14-bold">Redeemed</div>
          </div>
          <div className="coupon-code-card_coupon-status_text-font-12">
            {this.renderRedeemedCouponStatus({ type, dealItems, discountAmount })}
          </div>
        </div>
      );
    } else if (isDealExpired && moment(dealExpireTime).isBefore(currentDate)) {
      return (
        <div className="coupon-code-card_coupon-status">
          <div className="coupon-code-card_coupon-status_label">
            <img src={couponExpiredStamp} alt="expired-coupon" />
            <div className="coupon-code-card_coupon-status_text-font-14-bold">Expired</div>
          </div>
          <div className="coupon-code-card_coupon-status_text-font-12">
            {`Coupon expired on ${
              dealExpireTime ? formatDate(dealExpireTime) : formatDate(dealEndTime)
            }`}
          </div>
        </div>
      );
    } else if (moment(subscribeTime).isAfter()) {
      return (
        <div className="coupon-code-card_coupon-status">
          <div className="coupon-code-card_coupon-status_locked">
            {
              <>
                This coupon will unlock on <br />
                {`${formatDateTime(subscribeTime)}`}
              </>
            }
          </div>
        </div>
      );
    } else {
      return (
        <div className="coupon-code-card_coupon-status">
          <div className="coupon-code-card_coupon-status_coupon-code">{couponCode}</div>
        </div>
      );
    }
  };

  renderCouponStreakCTA = ({ subscribedDeal, sequenceNumber }) => {
    let couponStreakCTA = '';
    const subDealGuests = get(subscribedDeal, 'subDealGuests', []);
    const subDealGuestsObj = subDealGuests.find(
      (subDealGuest) => subDealGuest.dealSequence === sequenceNumber,
    );
    if (!isEmpty(subDealGuestsObj)) {
      const { expireTime, redeemTime, subscribeTime, isExpired, dealId } = subDealGuestsObj;
      couponStreakCTA = this.renderCouponCTA({
        isCouponRedeemed: redeemTime,
        isCouponSubscribed: subscribeTime,
        dealEndTime: expireTime,
        isDealExpired: isExpired,
        subscribeTime,
        dealId: dealId,
      });
    } else {
      couponStreakCTA = <div className="coupon-detail_text-font-14-bold">Coupon Code Locked</div>;
    }
    return couponStreakCTA;
  };

  renderCouponCTA = ({
    isCouponRedeemed,
    isCouponSubscribed,
    dealEndTime,
    isDealExpired,
    subscribeTime,
    dealId,
  }) => {
    const diffBetweenSubscribedAndExpiredDate = moment(dealEndTime).diff(
      moment(isCouponSubscribed),
      'days',
    );
    if (isCouponRedeemed) {
      return;
    } else if (isDealExpired) {
      return (
        <div className="coupon-detail_text_CTA" onClick={() => this.handleViewMoreCouponClick()}>
          {couponDetailConfig.CTA.viewMoreCoupon}
        </div>
      );
    } else if (isCouponSubscribed && diffBetweenSubscribedAndExpiredDate <= 7) {
      return (
        <div className="coupon-detail_text-font-14-bold">
          <span className="expiry-warning">{`Expiring on ${formatDate(dealEndTime)}`}</span>
        </div>
      );
    } else if (moment(subscribeTime).isAfter()) {
      return <></>;
    } else {
      return (
        <div className="coupon-detail_text-font-14-bold">Show this coupon code to avail offer</div>
      );
    }
  };

  renderCouponStreakStatus = (sequenceNumber) => {
    let status = '';
    const { subscribedDeal } = this.props;
    const subDealGuests = get(subscribedDeal, 'subDealGuests', []);
    const isCouponRedeemed = get(subDealGuests[sequenceNumber - 1], 'redeemTime', '');
    const isCouponSubscribed = get(subDealGuests[sequenceNumber - 1], 'subscribeTime', '');
    const couponExpireTime = get(subDealGuests[sequenceNumber - 1], 'expireTime', '');
    const isCouponExpired = get(subDealGuests[sequenceNumber - 1], 'isExpired', false);
    const diffBetweenSubscribedAndExpiredDate = moment(couponExpireTime).diff(
      moment(isCouponSubscribed),
      'days',
    );
    if (isCouponRedeemed) {
      status = 'redeemed';
    } else if (isCouponExpired) {
      status = 'expired';
    } else if (isCouponSubscribed && diffBetweenSubscribedAndExpiredDate <= 7) {
      status = 'expiring';
    } else if (isCouponSubscribed && moment(isCouponSubscribed).isAfter()) {
      status = 'unlocked';
    } else if (isCouponSubscribed) {
      status = 'active';
    } else {
      status = 'locked';
    }
    return status;
  };

  handleViewMoreCouponClick = () => {
    this.handeBackClick();
  };

  handleOutletTimingClick = (outletWorkhours) => {
    this.setState({
      showOutletTimingPopup: true,
      outletWorkhours,
    });
  };

  onCloseOutletTiminigPopup = () => {
    this.setState({
      showOutletTimingPopup: false,
      outletWorkhours: [],
    });
  };

  renderDiscountSection = ({ type, dealItems, discountAmount }) => {
    switch (type) {
      case offerTypeCode.DirectDiscount:
        return `${currencies.INR.symbol}${discountAmount}`;
      case offerTypeCode.ItemFree:
        const applicableItem =
          dealItems &&
          dealItems.length > 0 &&
          get(dealItems.find((item) => item) || {}, 'itemName', '');
        return `Free ${applicableItem}`;
      default:
        return;
    }
  };

  handleCouponStreakClick = (sequenceNumber) => {
    const streakCouponId = `streak-coupon-${sequenceNumber}`;
    const streakCouponElement = document.getElementById(streakCouponId);

    this.setState(
      {
        currentSelectCouponSequence: sequenceNumber,
      },
      () => {
        streakCouponElement.scrollIntoView(false);
      },
    );
  };

  handleOpenDownloadCouponPopupOpen = () => {
    this.setState({ openDownloadCouponPopup: true });
  };

  handleCreateCouponClone = (clonedDocument) => {
    if (clonedDocument) {
      const selectedElements = clonedDocument.querySelectorAll('#remove-from-coupon-img');
      selectedElements &&
        selectedElements.forEach((selectedElement) => {
          if (selectedElement) selectedElement.style.display = 'none';
        });
    }
  };

  handleDownloadCoupon = (type) => {
    switch (type) {
      case 'PDF':
        this.setState({ openDownloadCouponPopup: false }, () => {
          const couponNode = this.couponRef.current;
          html2canvas(couponNode, {
            allowTaint: true,
            useCORS: true, //Allow server hosted image in result
            onclone: this.handleCreateCouponClone,
          }).then((canvas) => {
            const imageName = get(this.props, 'subscribedDeal.couponCode', 'coupon');
            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = `${imageName}.png`;
            a.click();
          });
        });
        break;
      default:
        break;
    }
  };

  handleRedeemDetailsClick = (dealId) => {
    let name = '';
    let countryCode = '';
    let primaryPhoneNumber = '';
    let discountAmount = '';
    let orderAmount = '';
    let couponCode = '';
    let redeemTime = '';
    let discountValue = '';
    let discountMinValue = null;

    const { subscribedDeal } = this.props;
    const { guest, dealType, dealItems, type, value, minValue } = subscribedDeal;

    if (isEqual(dealType, dealTypes.streak)) {
      const subDealGuests = get(subscribedDeal, 'subDealGuests', []);
      const subDealGuestsObj = subDealGuests.find((subDealGuest) => subDealGuest.dealId === dealId);
      discountAmount = get(subDealGuestsObj, 'discountAmount', 0);
      orderAmount = get(subDealGuestsObj, 'orderAmount', 0);
      couponCode = get(subDealGuestsObj, 'couponCode', '');
      redeemTime = get(subDealGuestsObj, 'redeemTime', '');
    } else {
      discountAmount = get(subscribedDeal, 'discountAmount', 0);
      orderAmount = get(subscribedDeal, 'orderAmount', 0);
      couponCode = get(subscribedDeal, 'couponCode', '');
      redeemTime = get(subscribedDeal, 'redeemTime', '');
    }
    primaryPhoneNumber = get(subscribedDeal, 'guestPhone', '');
    countryCode = get(subscribedDeal, 'countryCode', '');
    name = get(subscribedDeal, 'guestName', '');
    discountValue = value;
    discountMinValue = minValue;

    const redeemData = {
      name,
      countryCode,
      primaryPhoneNumber,
      discountAmount,
      orderAmount,
      couponCode,
      redeemTime,
      type,
      dealItems,
      discountValue,
      discountMinValue,
    };
    this.setState({
      showRedemptionDetailsPopup: true,
      redeemData,
    });
  };

  onCloseRedemptionDetailsPopup = () => {
    this.setState({
      showRedemptionDetailsPopup: false,
    });
  };

  componentWillUnmount() {
    this.props.clearApiErrorResponse();
  }

  render() {
    const {
      greetingSplashScreen,
      subscribedDeal,
      appStartupInfo,
      readOnlyOutletDetails,
      weekdays,
      brandAdditionalInfo,
      deal,
    } = this.props;
    const {
      showErrorPopup,
      errorContent,
      handleErrorCTA,
      outletWorkhours,
      showOutletTimingPopup,
      initialAPIInteractionDone,
      currentSelectCouponSequence,
      showRedemptionDetailsPopup,
      redeemData,
      openDownloadCouponPopup,
    } = this.state;
    const brandName = toTitleCase(get(appStartupInfo, 'brand.name', ''));
    const brandLogo = get(appStartupInfo, 'brand.logoUrl', '');
    const brandStatus = get(appStartupInfo, 'brand.status', '');
    const brandPhoneNumber = get(appStartupInfo, 'brand.phone', '');
    const discountAmount = get(subscribedDeal, 'discountAmount', 0);
    const isCouponRedeemed = get(subscribedDeal, 'redeemTime', '');
    const isCouponSubscribed = get(subscribedDeal, 'subscribeTime', '');
    const dealEndTime = get(subscribedDeal, 'endTime', '');
    const dealExpireTime = get(subscribedDeal, 'expireTime', '');
    const dealItems = get(subscribedDeal, 'dealItems', []);
    const type = get(subscribedDeal, 'type', '');
    const dealType = get(subscribedDeal, 'dealType', '');
    const subDeals = get(subscribedDeal, 'subDeals', []);
    const couponCode = get(subscribedDeal, 'couponCode', '');
    const subDealGuests = get(subscribedDeal, 'subDealGuests', []);
    const isStreaxCouponAvailable = subDeals.some((subDeal) =>
      ['active', 'expiring'].includes(this.renderCouponStreakStatus(subDeal.sequenceNumber)),
    );
    const isDealExpired = get(deal, 'isExpired', false);
    return (
      <>
        {/* render greeting splash screen here */}
        {greetingSplashScreen && greetingSplashScreen}

        {initialAPIInteractionDone && (
          <div ref={this.couponRef} className="coupon-detail">
            {isEqual(brandStatus, 'DEMO') && (
              <div className="coupon-detail_banner">{'Demo Mode'}</div>
            )}
            <img
              id="remove-from-coupon-img"
              className="coupon-detail_back-btn"
              src={backIcon}
              alt={'<-'}
              onClick={this.handeBackClick}
            />

            <img className="coupon-detail_restaurant-logo" src={brandLogo} alt="brand-logo" />
            <div className="coupon-detail_restaurant-name">
              {get(appStartupInfo, 'brand.name', '')}
            </div>
            {isEqual(dealType, dealTypes.streak) && (
              <div className="coupon-detail_streak-section">
                {subDeals.map((subDeal, index) => (
                  <React.Fragment key={index}>
                    <StreaxCard
                      key={index}
                      showIcon={true}
                      subDeal={subDeal}
                      status={this.renderCouponStreakStatus(subDeal.sequenceNumber)}
                      selectedCouponSequence={currentSelectCouponSequence}
                      onCouponStreakClick={(sequenceNumber) =>
                        this.handleCouponStreakClick(sequenceNumber)
                      }
                    />
                  </React.Fragment>
                ))}
              </div>
            )}
            {isEqual(dealType, dealTypes.streak) ? (
              <div className="coupon-detail_streaks-coupon-section">
                {subDeals.map((subDeal, index) => (
                  <div className="coupon-detail_streaks-coupon-section_streak-coupon" key={index}>
                    <div className="coupon-detail_text-font-14">{`Your ${renderCount(
                      subDeal.sequenceNumber,
                    )} Order Coupon Code`}</div>
                    <CouponCodeCard
                      id={`streak-coupon-${subDeal.sequenceNumber}`}
                      deal={subscribedDeal}
                      couponDescription={this.renderStreakCouponDescription({
                        subscribedDeal,
                        sequenceNumber: subDeal.sequenceNumber,
                        activeDayCounter: subDeal.activeDayCounter,
                      })}
                    />
                    <div className="coupon-detail_status-section">
                      {this.renderCouponStreakCTA({
                        subscribedDeal,
                        sequenceNumber: subDeal.sequenceNumber,
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <React.Fragment>
                <div className="coupon-detail_text-font-14">Your Coupon Code</div>
                <CouponCodeCard
                  deal={subscribedDeal}
                  couponDescription={this.renderCouponStatus({
                    couponCode,
                    discountAmount,
                    isCouponRedeemed,
                    dealEndTime,
                    isDealExpired,
                    dealItems,
                    type,
                    dealExpireTime,
                  })}
                />
                <div className="coupon-detail_status-section">
                  {this.renderCouponCTA({
                    isCouponRedeemed,
                    isCouponSubscribed,
                    dealEndTime,
                    isDealExpired,
                  })}
                </div>
              </React.Fragment>
            )}
            {((!isEqual(dealType, dealTypes.streak) && !isCouponRedeemed && !isDealExpired) ||
              (isEqual(dealType, dealTypes.streak) && isStreaxCouponAvailable)) && (
              <Button
                id="remove-from-coupon-img"
                className="download-coupon-cta"
                content={couponDetailConfig.downloadCouponCTA}
                onClick={this.handleOpenDownloadCouponPopupOpen}
              />
            )}
            {!isDealExpired && (
              <div id="remove-from-coupon-img" className="coupon-detail_share-coupon">
                <ShareCoupon onShareCTAClick={this.handelShareCTAClick} />
              </div>
            )}
            {(!isDealExpired || !isCouponRedeemed) && (
              <>
                {!isEmpty(subscribedDeal) && (
                  <CouponTnC
                    deal={subscribedDeal}
                    currentSelectCouponSequence={currentSelectCouponSequence}
                    subDealGuests={subDealGuests}
                    weekdays={weekdays}
                    typeOf="arrOfString"
                  />
                )}
                <div className="coupon-detail_section-break" />
                <div className="coupon-detail_redeem-coupon-guide">
                  <div className="coupon-detail_redeem-coupon-guide_heading">
                    How to Redeem Coupon
                  </div>
                  <div className="coupon-detail_redeem-coupon-guide_list-item">
                    <ul>
                      <li>
                        Visit the restaurant and show the coupon code before placing your order
                      </li>
                      <li>
                        {`The restaurant will redeem your coupon and provide applicable ${
                          isEqual(type, offerTypeCode.ItemFree) ? 'item' : 'discount'
                        }`}
                      </li>
                    </ul>
                  </div>
                </div>
              </>
            )}
            <div className="coupon-detail_section-break" />
            <CommunicationInfo
              id="remove-from-coupon-img"
              brandName={brandName}
              outletDetails={readOnlyOutletDetails}
              applicableDealOutlets={get(subscribedDeal, 'dealOutlets', [])}
              onOutletTimingClick={(outletWorkhours) =>
                this.handleOutletTimingClick(outletWorkhours)
              }
              brandPhoneNumber={brandPhoneNumber}
              brandAdditionalInfo={brandAdditionalInfo}
            />
            <div className="coupon-detail_section-break" />
            <Footer />
            {showErrorPopup && (
              <ExceptionComponent
                openSegmentPopup={showErrorPopup}
                dismissSegmentPopup={this.onCloseExceptionComponent}
                title={get(errorContent, 'title')}
                description={get(errorContent, 'description')}
                buttonCta={get(errorContent, 'buttonCta')}
                handleButtonCta={this.handleExceptionCTA}
              />
            )}
            {showOutletTimingPopup && (
              <OutletTimingPopup
                openSegmentPopup={showOutletTimingPopup}
                dismissSegmentPopup={this.onCloseOutletTiminigPopup}
                outletWorkhours={outletWorkhours}
              />
            )}
            {showRedemptionDetailsPopup && (
              <RedemptionDetailsPopup
                openSegmentPopup={showRedemptionDetailsPopup}
                dismissSegmentPopup={this.onCloseRedemptionDetailsPopup}
                redeemData={redeemData}
              />
            )}
            {openDownloadCouponPopup && (
              <DownloadCouponPopup
                open={openDownloadCouponPopup}
                onClose={() => this.setState({ openDownloadCouponPopup: false })}
                handleDownload={this.handleDownloadCoupon}
              />
            )}
          </div>
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    errorResponse: get(state, 'common.errorResponse', {}),
    appStartupInfo: get(state, 'common.appStartupInfo', {}),
    subscribedDeal: get(state, 'app.subscribedDeal', {}),
    readOnlyOutletDetails: get(state, 'common.outletDetails._embedded.restaurants', []),
    weekdays: get(state, 'app.masterData.weekdays', []),
    brandAdditionalInfo: get(state, 'common.appStartupInfo.brand.additionalData', {}),
    deal: get(state, 'app.brandDeal', {}),
  }),
  {
    fetchSubscribedGuestDeal: fetchSubscribedGuestDealAction,
    clearApiErrorResponse: clearApiErrorResponseAction,
    updateDealGuest: updateDealGuestAction,
    fetchOutletDetails: fetchOutletDetailsAction,
    fetchDealById: fetchDealByIdAction,
    fetchAppStartupInfo: fetchAppStartupInfoAction,
  },
)(DealSuccess);
